import { clamp } from '../core/math';
import type { Point } from '../core/types';
import { getTipImage } from './library';
import { SpacingWalker } from './path';
import { createCoverage, jitterStamp, makeRandom, rasterizeTip, stampCoverage } from './tip';
import type { CoverageBitmap } from './tip';
import type { BrushDynamics, BrushTip } from './types';

/** Shape of the brush a preview needs, so both presets and live options fit. */
export interface PreviewBrush {
  size: number;
  tip: BrushTip;
  dynamics: BrushDynamics;
  opacity: number;
  flow: number;
}

/** Single stamp of the tip, fitted into a square thumbnail. */
export function renderTipThumbnail(tip: BrushTip, box: number): CoverageBitmap {
  const bitmap = createCoverage(box, box);
  const stamp = rasterizeTip(tip, box - 2, getTipImage(tip.imageId));
  stampCoverage(bitmap, stamp, box / 2, box / 2, 1);
  return bitmap;
}

/**
 * Draws the sample stroke shown at the bottom of the Brush panel: a tapering
 * S-curve, so spacing, jitter and pressure are all visible at a glance.
 */
export function renderStrokePreview(
  brush: PreviewBrush,
  width: number,
  height: number,
): CoverageBitmap {
  const bitmap = createCoverage(width, height);
  const random = makeRandom(0x51a7);
  const walker = new SpacingWalker();
  const image = getTipImage(brush.tip.imageId);
  const cache = new Map<string, CoverageBitmap>();

  // Keep the largest stamp inside the strip, whatever size the brush is set to.
  const scale = Math.min(1, (height - 4) / Math.max(1, brush.size));
  const size = Math.max(1, brush.size * scale);
  const amplitude = Math.max(0, (height - size) / 2 - 1);

  const at = (t: number): Point => ({
    x: 2 + t * (width - 4),
    y: height / 2 - Math.sin(t * Math.PI * 2) * amplitude,
  });

  const emit = (point: Point, direction: number, pressure: number): void => {
    const placement = jitterStamp(
      {
        x: point.x,
        y: point.y,
        size: brush.dynamics.pressureSize ? size * clamp(pressure, 0.08, 1) : size,
        angle: brush.tip.angle,
        roundness: brush.tip.roundness,
        flow: brush.dynamics.pressureOpacity ? brush.flow * pressure : brush.flow,
      },
      brush.dynamics,
      direction,
      random,
    );
    const key = `${Math.round(placement.size)}:${Math.round(placement.angle)}:${placement.roundness.toFixed(2)}`;
    let stamp = cache.get(key);
    if (!stamp) {
      stamp = rasterizeTip(
        { ...brush.tip, angle: placement.angle, roundness: placement.roundness },
        placement.size,
        image,
      );
      cache.set(key, stamp);
    }
    stampCoverage(bitmap, stamp, placement.x, placement.y, placement.flow);
  };

  const steps = 240;
  const step = Math.max(0.5, size * Math.max(0.01, brush.tip.spacing));
  let pressure = 0;
  walker.begin(at(0), step, (point, direction) => emit(point, direction, 0.08));
  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;
    pressure = Math.sin(t * Math.PI);
    walker.extend(at(t), step, (point, direction) => emit(point, direction, pressure));
  }

  // The whole stroke is limited by the opacity, exactly as on commit.
  if (brush.opacity < 1) {
    for (let i = 0; i < bitmap.data.length; i += 1) {
      bitmap.data[i] = Math.round(bitmap.data[i] * brush.opacity);
    }
  }
  return bitmap;
}

/** Paints a coverage bitmap into RGBA pixels of one colour, for canvas previews. */
export function coverageToImageData(
  bitmap: CoverageBitmap,
  colour: { r: number; g: number; b: number },
): ImageData {
  const pixels = new Uint8ClampedArray(bitmap.width * bitmap.height * 4);
  for (let i = 0; i < bitmap.data.length; i += 1) {
    pixels[i * 4] = colour.r * 255;
    pixels[i * 4 + 1] = colour.g * 255;
    pixels[i * 4 + 2] = colour.b * 255;
    pixels[i * 4 + 3] = bitmap.data[i];
  }
  return new ImageData(pixels, bitmap.width, bitmap.height);
}
